import React, { useState, useEffect, useRef } from 'react';
import { ActivityIndicator } from 'react-native';
import { useTranslation } from 'react-i18next';
import AsyncStorage from '../AsyncStorageShim';
import ListsManager from '../services/ListsManager';
import AnalyticsService from '../services/AnalyticsService';
import { getAuth, signOut } from 'firebase/auth';
import { useTheme } from '../theme/ThemeContext';
import { useOrg } from '../hooks/useOrg';
import {
  FaTwitter, FaPencilAlt, FaHistory, FaGlobe,
  FaCommentDots, FaFileAlt, FaLock, FaSignOutAlt, FaChevronRight,
} from 'react-icons/fa';
import Modal from '../components/Modal';
import WritingStyleScreen from './WritingStyleScreen';
import PastCasesScreen from './PastCasesScreen';
import LanguagesScreen from './LanguagesScreen';

let T = {};

// ── Inline Terms content ──────────────────────────────────────────────────────

const TermsContent = () => {
  const { t } = useTranslation();
  return (
  <div style={{ padding: '28px 32px', maxWidth: 720 }}>
    <div style={{ color: T.text, fontSize: 26, fontWeight: 800, marginBottom: 4, letterSpacing: -0.5 }}>{t('app.profile.termsOfUse')}</div>
    <div style={{ color: '#3b82f6', fontSize: 12, fontWeight: 600, marginBottom: 32, textTransform: 'uppercase', letterSpacing: 1 }}>Updated February 26th 2026</div>
    {[
      { h: '1. Terms of Use', body: [
        { sub: '1.1 Registration and Use', p: 'The use of the Application is conditional upon acceptance of the Terms of Use and Privacy Policy. Users must provide accurate and truthful information, as required.' },
        { sub: '1.2 Usage Restrictions', p: 'It is prohibited to use the Application for illegal, harmful, or privacy-violating purposes. Reverse engineering, duplication, or alteration of the Application\'s source code is strictly prohibited.' },
      ]},
      { h: '2. Copyright and Intellectual Property', body: [{ p: 'All copyrights related to content, designs, and code in the Application are owned by the Company. Users are prohibited from copying, duplicating, or distributing the information without prior approval.' }]},
      { h: '3. Limitation of Liability', body: [
        { sub: '3.1 Content Responsibility', p: 'The application is not responsible for content originating from the X (Twitter) network, the AI tool used to analyze tweets, or any other third-party service integrated into the system.' },
        { sub: '3.2 Interruptions or Malfunctions', p: 'The company shall not be held liable for any indirect, incidental, or consequential damages that may arise due to technical malfunctions, temporary service outages, or inaccuracies in the provided information.' },
        { sub: '3.3 Improper Use', p: 'Users are solely responsible for using the system in compliance with laws and regulations. The Application reserves the right to block or suspend users who violate these guidelines.' },
      ]},
      { h: '4. Changes to Terms', body: [{ p: 'The Application reserves the right to update its Terms of Use and Privacy Policy at any time, with prior notice to users.' }]},
      { h: '5. Termination of Service', body: [{ p: 'The Application may terminate services for users found to violate terms or misuse the Application. In the event of service termination, the Application does not guarantee the recovery of lost data.' }]},
      { h: '6. Dispute Resolution', body: [{ p: 'Any disputes related to the use of the Application are subject to Israeli law and will be resolved in Israeli courts.' }]},
      { h: '7. Minors', body: [{ p: 'The Application is intended for users aged 16 and above.' }]},
      { h: '8. Support', body: [{ p: 'Support services operate from 08:00 to 20:00. The Company is committed to resolving issues within 24 hours of submission.' }]},
    ].map(s => (
      <div key={s.h} style={{ marginBottom: 24 }}>
        <div style={{ color: T.text, fontSize: 16, fontWeight: 700, marginBottom: 10, background: T.surface, padding: '4px 10px', borderRadius: 6, display: 'inline-block' }}>{s.h}</div>
        {s.body.map((b, i) => (
          <div key={i}>
            {b.sub && <div style={{ color: T.text, fontSize: 13, fontWeight: 600, marginBottom: 4, marginTop: 8 }}>{b.sub}</div>}
            <div style={{ color: T.textSec, fontSize: 14, lineHeight: '22px', marginBottom: 6 }}>{b.p}</div>
          </div>
        ))}
      </div>
    ))}
    <div style={{ marginTop: 24, padding: 20, background: 'rgba(59,130,246,0.05)', borderRadius: 16, border: '1px solid rgba(59,130,246,0.15)' }}>
      <div style={{ color: T.text, fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{t('app.profile.contactUs')}</div>
      <div style={{ color: T.textSec, fontSize: 14 }}>{t('app.profile.emailUsAt')} <span style={{ color: '#3b82f6' }}>zohar@reactfirst.ai</span></div>
    </div>
  </div>
  );
};

// ── Inline Privacy content ────────────────────────────────────────────────────

const PrivacyContent = () => {
  const { t } = useTranslation();
  return (
  <div style={{ padding: '28px 32px', maxWidth: 720 }}>
    <div style={{ color: T.text, fontSize: 26, fontWeight: 800, marginBottom: 4, letterSpacing: -0.5 }}>{t('app.profile.privacyPolicy')}</div>
    <div style={{ color: '#3b82f6', fontSize: 12, fontWeight: 600, marginBottom: 32, textTransform: 'uppercase', letterSpacing: 1 }}>Updated February 26th 2026</div>
    {[
      { h: '1.1 Data Collection', p: 'The Application does not collect personally identifiable information except for information required for operational purposes and service improvement. The username and password are provided personally and are not linked to identifiable information.' },
      { h: '1.2 Use of Information', p: 'The collected data is used solely for providing services and improving user experience. The Application does not share data with third parties nor use it for commercial purposes.' },
      { h: '1.3 Data Security', p: 'The Company is committed to maintaining data confidentiality and protecting it through advanced encryption and security protocols. In case of a data breach, the Company commits to notifying users and authorities within 72 hours.' },
      { h: '1.4 User Rights', p: 'In accordance with Israeli privacy laws (and where applicable, GDPR and CCPA), users have the right to access, correct, or delete their collected data. Requests for data deletion can be submitted through the support center.' },
    ].map(s => (
      <div key={s.h} style={{ marginBottom: 24 }}>
        <div style={{ color: T.text, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{s.h}</div>
        <div style={{ color: T.textSec, fontSize: 14, lineHeight: '22px' }}>{s.p}</div>
      </div>
    ))}
    <div style={{ marginTop: 24, padding: 20, background: 'rgba(59,130,246,0.05)', borderRadius: 16, border: '1px solid rgba(59,130,246,0.15)' }}>
      <div style={{ color: T.text, fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{t('app.profile.contactUs')}</div>
      <div style={{ color: T.textSec, fontSize: 14 }}>{t('app.profile.emailUsAt')} <span style={{ color: '#3b82f6' }}>zohar@reactfirst.ai</span></div>
    </div>
  </div>
  );
};

// ── Main screen ───────────────────────────────────────────────────────────────

const ProfileScreen = () => {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const { activeOrg } = useOrg();

  T = {
    bg:        theme.bg,
    card:      theme.bgCard,
    surface:   theme.bgSurface,
    border:    theme.border,
    text:      theme.textPrimary,
    textSec:   theme.textSecondary,
    textMuted: theme.textMuted,
    primary:   '#0a84ff',
    error:     '#ff453a',
    success:   '#30d158',
  };

  const [loading, setLoading]               = useState(true);
  const [isLoggingOut, setIsLoggingOut]     = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [userName, setUserName]             = useState('');
  const [userEmail, setUserEmail]           = useState('');
  const [listsCount, setListsCount]         = useState(0);
  const [twitterCount, setTwitterCount]     = useState(0);
  const [telegramCount, setTelegramCount]   = useState(0);
  const [xAccounts, setXAccounts]           = useState([]);
  const [isXLoading, setIsXLoading]         = useState(false);
  const [error, setError]                   = useState('');
  const [selectedPanel, setSelectedPanel]   = useState(null);
  const [sidebarW, setSidebarW]             = useState(320);
  const dragRef      = useRef(null);
  const dragStartRef = useRef(null);

  const startDrag = (e) => {
    e.preventDefault();
    dragRef.current = true;
    dragStartRef.current = { x: e.clientX, sidebarW };
    const onMove = (mv) => {
      const rawDx = mv.clientX - dragStartRef.current.x;
      const dx = i18n.language === 'he' ? -rawDx : rawDx;
      setSidebarW(Math.max(240, Math.min(560, dragStartRef.current.sidebarW + dx)));
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  useEffect(() => { fetchUserData(); fetchXAccounts(); AnalyticsService.logActivity('view_profile'); }, []); // eslint-disable-line

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const auth = getAuth();
      const firebaseUser = auth.currentUser;
      if (firebaseUser) {
        setUserName(firebaseUser.displayName || firebaseUser.email.split('@')[0]);
        setUserEmail(firebaseUser.email);
      } else {
        setUserName(await AsyncStorage.getItem('name') || 'User');
        setUserEmail(await AsyncStorage.getItem('email') || '');
      }
      const [lists, twitter, telegram] = await Promise.all([
        ListsManager.fetchLists(activeOrg?.id),
        ListsManager.fetchAllTwitterUsernames(),
        ListsManager.fetchAllTelegramChannels(),
      ]);
      setListsCount(lists?.length || 0);
      setTwitterCount(twitter?.length || 0);
      setTelegramCount(telegram?.length || 0);
    } catch (err) {
      console.error('Error fetching user data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchXAccounts = async () => {
    try {
      const { accounts } = await ListsManager.getXAccounts();
      setXAccounts(accounts || []);
    } catch (err) {
      console.error('Error fetching X accounts:', err);
    }
  };

  const handleConnectX = async () => {
    try {
      setIsXLoading(true);
      const data = await ListsManager.initiateXAuth();
      if (data?.authorization_url) window.location.href = data.authorization_url;
      else throw new Error('No authorization URL received');
    } catch (err) {
      setError('Failed to connect to X. Please try again.');
      setIsXLoading(false);
    }
  };

  const handleDisconnectX = async (username) => {
    try {
      setIsXLoading(true);
      await ListsManager.disconnectXAccount(username);
      await fetchXAccounts();
    } catch (err) {
      setError('Failed to disconnect X account.');
    } finally {
      setIsXLoading(false);
    }
  };

  const logoutUser = async () => {
    try {
      setIsLoggingOut(true);
      try { await ListsManager.removeFCMToken(); } catch {}
      if (ListsManager.savedListenerId) ListsManager.savedListenerId = null;
      try {
        const auth = getAuth();
        if (auth.currentUser) await signOut(auth);
      } catch {}
      await AsyncStorage.removeItem('userToken');
      await AsyncStorage.clear();
      window.location.replace('/welcome');
    } catch (err) {
      setError('Failed to log out');
      setIsLoggingOut(false);
    }
  };

  const primaryXAccount = xAccounts.find(a => a.is_primary) || xAccounts[0];

  const settingsGroups = [
    {
      title: t('app.profile.account'),
      items: [
        primaryXAccount
          ? { icon: FaTwitter, label: `@${primaryXAccount.x_username}`, sub: t('app.profile.disconnectXSub'), color: '#1da1f2', action: () => handleDisconnectX(primaryXAccount.x_username), loading: isXLoading }
          : { icon: FaTwitter, label: t('app.profile.connectX'), sub: t('app.profile.connectXSub'), color: '#1da1f2', action: handleConnectX, loading: isXLoading },
        { icon: FaPencilAlt, label: t('app.profile.writingStyle'), sub: t('app.profile.writingStyleSub'), color: '#818cf8', panel: 'writing-style', action: () => setSelectedPanel('writing-style') },
        { icon: FaHistory, label: t('app.profile.pastActions'), sub: t('app.profile.pastActionsSub'), color: '#f59e0b', panel: 'past-cases', action: () => setSelectedPanel('past-cases') },
        { icon: FaGlobe, label: t('app.profile.chooseLanguage'), sub: t('app.profile.chooseLanguageSub'), color: '#4A90E2', panel: 'languages', action: () => setSelectedPanel('languages') },
      ],
    },
    {
      title: t('app.profile.support'),
      items: [
        { icon: FaCommentDots, label: t('app.profile.contactUs'), sub: t('app.profile.contactUsSub'), color: '#50E3C2', action: () => window.open('mailto:zohar@reactfirst.ai', '_blank') },
        { icon: FaFileAlt, label: t('app.profile.termsOfUse'), sub: t('app.profile.termsOfUseSub'), color: '#F5A623', panel: 'terms', action: () => setSelectedPanel('terms') },
        { icon: FaLock, label: t('app.profile.privacyPolicy'), sub: t('app.profile.privacyPolicySub'), color: '#7ED321', panel: 'privacy', action: () => setSelectedPanel('privacy') },
      ],
    },
  ];

  if (loading || isLoggingOut) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: T.bg, flexDirection: 'column', gap: 12 }}>
        <ActivityIndicator size="large" color={T.primary} />
        <span style={{ color: T.textMuted, fontSize: 13 }}>{isLoggingOut ? t('app.profile.loggingOut') : t('app.profile.loading')}</span>
      </div>
    );
  }

  const initial = (userName || '?')[0].toUpperCase();

  const css = `
    .prof-row { transition: background 0.12s; cursor: pointer; }
    .prof-row:hover { background: ${T.surface} !important; }
    .prof-item { transition: background 0.12s; cursor: pointer; }
    .prof-item:hover { background: ${T.surface} !important; }
    .prof-item.active { background: rgba(10,132,255,0.08) !important; }
    .prof-divider { width: 5px; flex-shrink: 0; cursor: col-resize; background: transparent; position: relative; z-index: 10; transition: background 0.15s; }
    .prof-divider:hover, .prof-divider:active { background: ${T.primary}55; }
    .prof-divider::after { content: ''; position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); width: 1px; height: 32px; background: ${T.border}; border-radius: 1px; }
    ::-webkit-scrollbar { width: 4px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: rgba(128,128,128,0.2); border-radius: 2px; }
  `;

  const renderRightPanel = () => {
    if (selectedPanel === 'writing-style') return <WritingStyleScreen onBack={() => setSelectedPanel(null)} />;
    if (selectedPanel === 'past-cases') return <PastCasesScreen onBack={() => setSelectedPanel(null)} />;
    if (selectedPanel === 'languages') return <LanguagesScreen onBack={() => setSelectedPanel(null)} />;
    if (selectedPanel === 'terms') return <div style={{ flex: 1, overflowY: 'auto' }}><TermsContent /></div>;
    if (selectedPanel === 'privacy') return <div style={{ flex: 1, overflowY: 'auto' }}><PrivacyContent /></div>;

    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        <div style={{ width: 56, height: 56, borderRadius: 18, background: 'rgba(10,132,255,0.08)', border: '1px solid rgba(10,132,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FaPencilAlt size={22} color={T.textMuted} />
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: T.text, fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{t('app.profile.selectSetting')}</div>
          <div style={{ color: T.textMuted, fontSize: 13 }}>{t('app.profile.chooseOption')}</div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <div style={{ display: 'flex', height: '100vh', background: T.bg, overflow: 'hidden' }}>

        {/* ── Left column ── */}
        <div style={{
          width: sidebarW, flexShrink: 0,
          display: 'flex', flexDirection: 'column',
          overflowY: 'auto',
        }}>

          {/* Avatar + name */}
          <div style={{ padding: '28px 20px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 72, height: 72, borderRadius: 24,
              background: 'rgba(10,132,255,0.12)',
              border: '1px solid rgba(10,132,255,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ color: T.primary, fontSize: 28, fontWeight: 800 }}>{initial}</span>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: T.text, fontSize: 17, fontWeight: 700, marginBottom: 2 }}>{userName}</div>
              <div style={{ color: T.textMuted, fontSize: 12 }}>{userEmail}</div>
            </div>
          </div>


          {/* Settings groups */}
          <div style={{ padding: '20px 20px 0' }}>
            {settingsGroups.map(group => (
              <div key={group.title} style={{ marginBottom: 20 }}>
                <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
                  {group.title}
                </div>
                <div style={{ borderRadius: 12, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
                  {group.items.map((item, i) => {
                    const Icon = item.icon;
                    const isActive = item.panel != null && item.panel === selectedPanel;
                    return (
                      <div
                        key={item.label}
                        className={`prof-item${isActive ? ' active' : ''}`}
                        onClick={item.loading ? undefined : item.action}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '12px 14px', background: T.card,
                          borderBottom: i < group.items.length - 1 ? `1px solid ${T.border}` : 'none',
                          opacity: item.loading ? 0.6 : 1,
                        }}
                      >
                        <div style={{
                          width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                          background: `${item.color}18`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Icon size={13} color={item.color} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: T.text, fontSize: 12, fontWeight: 600, marginBottom: 1 }}>{item.label}</div>
                          <div style={{ color: T.textMuted, fontSize: 10, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{item.sub}</div>
                        </div>
                        {item.loading
                          ? <ActivityIndicator size="small" color={item.color} />
                          : <FaChevronRight size={9} color={isActive ? T.primary : T.textMuted} />}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Logout */}
          <div style={{ padding: '0 20px 20px' }}>
            <button
              onClick={() => setShowLogoutConfirm(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '11px 14px', borderRadius: 12, width: '100%',
                background: 'rgba(255,69,58,0.08)', border: '1px solid rgba(255,69,58,0.25)',
                cursor: 'pointer',
              }}
            >
              <FaSignOutAlt size={13} color={T.error} />
              <span style={{ color: T.error, fontSize: 13, fontWeight: 700 }}>{t('app.profile.logOut')}</span>
            </button>

            {error && (
              <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 10, background: 'rgba(255,69,58,0.08)', border: '1px solid rgba(255,69,58,0.25)', color: T.error, fontSize: 12 }}>
                {error}
              </div>
            )}
          </div>
        </div>

        {/* ── Divider ── */}
        <div className="prof-divider" onMouseDown={startDrag} />

        {/* ── Right panel ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: T.bg }}>
          {renderRightPanel()}
        </div>
      </div>

      {/* Logout confirmation */}
      <Modal isVisible={showLogoutConfirm} onClose={() => setShowLogoutConfirm(false)} title={t('app.profile.confirmLogout')}>
        <div style={{ padding: 24 }}>
          <p style={{ color: T.textSec, fontSize: 14, marginBottom: 20, textAlign: 'center' }}>
            {t('app.profile.logOutConfirm')}
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => setShowLogoutConfirm(false)}
              style={{ flex: 1, padding: '11px 0', borderRadius: 10, background: T.surface, border: `1px solid ${T.border}`, color: T.text, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              {t('app.profile.cancel')}
            </button>
            <button
              onClick={() => { setShowLogoutConfirm(false); logoutUser(); }}
              style={{ flex: 1, padding: '11px 0', borderRadius: 10, background: 'rgba(255,69,58,0.12)', border: '1px solid rgba(255,69,58,0.3)', color: T.error, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
            >
              {t('app.profile.logOut')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ProfileScreen;
